// src/pages/clab-biodata-approved/index.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import StatusIndicatorBadge from "components/ui/StatusIndicatorBadge";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
// import { biodataService } from 'utils/supabaseServices';
// TODO: Migrate biodataService usages to backend API
import { useAuth } from "context/AuthContext";
import { getUserRoleForComponents } from "utils/authHelpers";
import BiodataDetailsView from "components/BiodataDetailsView";
import FilterPanel from "./components/FilterPanel";
import { clabAPI, rejectionReasonAPI, biodataAPI } from "utils/api";
import { useBiodataCache } from "context/BiodataCacheContext";
import PaginationComponent from "components/PaginationComponent";
import Notifier from "components/ui/Notifier";
import { useNotifications } from "hooks/useNotifications";
import { useBiodataManagement } from "hooks/useBiodataManagement";
import { useNotifier } from '../../context/NotificationContext';

const ClabBiodataApproved = () => {
  const notify = useNotifier();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const userRole = getUserRoleForComponents(user);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [biodataList, setBiodataList] = useState([]);
  const [biodataPagination, setBiodataPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: "",
    nationality: "",
    recruiter_id: "",
    date_from: "",
    date_to: "",
    skill: "",
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBiodataDetails, setSelectedBiodataDetails] = useState(null);
  const [exportScope, setExportScope] = useState("all");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectForm, setRejectForm] = useState({ reason_id: "", remarks: "" });
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [rejectionReasonsLoading, setRejectionReasonsLoading] = useState(false);
  const {
    notifications,
    showSuccess,
    showError,
    removeNotification: dismissNotification,
  } = useNotifications();
  const {
    getBiodataByStatus,
    invalidateOnUpdate,
    getPhotoUrl,
    prefetchPhotoUrl,
  } = useBiodataCache();

  // Initialize biodata management hook for export functionality
  const { exportBiodata, exportStatus } = useBiodataManagement({
    apiEndpoints: {
      list: "/biodatas/status/approved",
      export: "/biodatas/export",
      print: "/biodatas/print",
    },
    biodataType: "approved",
    enableDataFetching: false, // Don't fetch data, we already have it
  });

  const handleExportData = async () => {
    try {
      if (exportScope === "current_page" && biodataList.length === 0) {
        showError("No biodata on the current page to export");
        return;
      }

      const exportFilters = exportScope === "filtered" ? filters : undefined;
      const pagination =
        exportScope === "current_page"
          ? { page: currentPage, perPage: itemsPerPage }
          : undefined;

      await exportBiodata([], "excel", {
        statusFilter: exportScope === "all" ? "all" : "approved",
        includeDocuments: false,
        includePhotos: false,
        useBackend: true,
        exportScope,
        filters: exportFilters,
        pagination,
      });
    } catch (error) {
      console.error("Export failed:", error);
      showError("Export failed: " + error.message);
    }
  };

  const handleBulkPrint = async (printAll = false) => {
    try {
      // Show loading state
      setLoading(true);

      const token = localStorage.getItem("authToken");
      const API_BASE_URL =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

      if (!token) {
        showError("Authentication token not found. Please log in again.");
        return;
      }

      let ids = [];

      if (printAll) {
        // Fetch ALL approved biodata IDs (not just current page)
        showSuccess("Fetching all approved biodata records...");

        // Use biodataAPI helper instead of direct fetch to avoid URL issues
        const allBiodataResponse = await biodataAPI.getBiodataByStatus(
          "approved",
          {
            page: 1,
            per_page: 10000,
          }
        );

        if (!allBiodataResponse.success) {
          throw new Error("Failed to fetch all approved biodata");
        }

        const allRecords =
          allBiodataResponse.records || allBiodataResponse.data || [];

        if (allRecords.length === 0) {
          showError("No approved biodata found");
          return;
        }

        ids = allRecords.map((b) => b.id);
        showSuccess(
          `Found ${ids.length} approved biodata records. Generating PDFs...`
        );
      } else {
        // Use current page only
        if (biodataList.length === 0) {
          showError("No biodata to print on current page");
          return;
        }
        ids = biodataList.map((b) => b.id);
      }

      // Call bulk print API
      const bulkPrintUrl = `${API_BASE_URL}/biodatas/bulk-print`;

      const response = await fetch(bulkPrintUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ids,
          format: "zip",
        }),
      });

      if (!response.ok) {
        // Try to parse error as JSON, but fallback to text if it fails
        let errorMessage = `Failed to generate bulk print (Status: ${response.status})`;
        let errorDetails = "";

        try {
          const errorData = await response.json();
          console.error("[Bulk Print] Error (JSON):", errorData);
          errorMessage = errorData.message || errorData.error || errorMessage;
          errorDetails = JSON.stringify(errorData, null, 2);
        } catch (e) {
          // If JSON parsing fails, try to get error as text
          try {
            const errorText = await response.text();
            console.error("[Bulk Print] Error (Text):", errorText);
            errorMessage = errorText.substring(0, 200) || errorMessage;
            errorDetails = errorText;
          } catch (textError) {
            console.error("[Bulk Print] Could not parse error:", textError);
          }
        }

        console.error("[Bulk Print] Full error:", errorDetails);
        throw new Error(errorMessage);
      }

      // Check content type to ensure we got a ZIP file
      const contentType = response.headers.get("content-type");

      // Get the blob (ZIP file)
      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error(
          "Received empty file from server. Please check server logs."
        );
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split("T")[0];
      a.download = `approved_biodata_${timestamp}_${ids.length}_records.zip`;

      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showSuccess(
        `Successfully generated ${ids.length} HTML files in ZIP archive! Extract and open in browser to print as PDF.`
      );
    } catch (error) {
      console.error("Bulk print failed:", error);
      showError("Bulk print failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadApprovedBiodata = async () => {
      try {
        setLoading(true);
        setError(null);
        // Build query params from filters and currentPage
        const params = {
          page: currentPage,
          per_page: itemsPerPage,
          search: filters.search || undefined,
          nationality: filters.nationality || undefined,
          recruiter_id: filters.recruiter_id || undefined,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          skill: filters.skill || undefined,
        };

        const result = await getBiodataByStatus("approved", params);

        if (result.fromCache) {
        }

        if (isMounted) {
          if (result.success) {
            const items = Array.isArray(result.records)
              ? result.records
              : Array.isArray(result.data?.data)
              ? result.data.data
              : [];
            setBiodataList(items);
            setBiodataPagination(result.pagination);
          } else {
            setError(
              result.error ||
                result.message ||
                "Failed to load approved biodata"
            );
          }
        }
      } catch (error) {
        if (isMounted) {
          setError("Failed to load approved biodata");
          console.error("Error loading approved biodata:", error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadApprovedBiodata();

    return () => {
      isMounted = false;
    };
  }, [filters, currentPage, itemsPerPage]);

  // When filters change, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // When items per page changes, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // Progressive photo prefetch when avatars enter viewport
  useEffect(() => {
    const elements = document.querySelectorAll("[data-biodata-id]");
    if (!elements || elements.length === 0) return;
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idAttr = entry.target.getAttribute("data-biodata-id");
          const id = Number(idAttr) || idAttr;
          if (!id || seen.has(id)) return;
          const rec = biodataList.find((b) => b.id === id);
          const direct = rec?.photo_url || rec?.primary_photo_url;
          const cached = getPhotoUrl(id, rec);
          if (!direct && !cached) prefetchPhotoUrl(id);
          seen.add(id);
          observer.unobserve(entry.target);
        });
      },
      { root: null, threshold: 0.25 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [biodataList, getPhotoUrl, prefetchPhotoUrl]);

  // Redirect if not CLAB user
  useEffect(() => {
    if (
      userProfile &&
      !["clab_user", "clab", "admin", "master_admin", "1", 1].includes(
        userProfile.role
      )
    ) {
      navigate("/login-screen");
    }
  }, [userProfile, navigate]);

  // Use backend pagination
  const paginatedBiodata = biodataList;

  const handleViewDetails = async (biodata) => {
    try {
      const result = await biodataAPI.getBiodataById(biodata.id);
      setSelectedBiodataDetails(result.data);
      setShowDetailsModal(true);
    } catch (error) {
      setError("Failed to load biodata details");
    }
  };

  const handlePrint = (biodata) => {
    try {
      // Enhanced print functionality using BiodataPrintView structure
      const printWindow = window.open(
        "",
        "_blank",
        "width=800,height=600,scrollbars=yes,resizable=yes"
      );

      if (!printWindow) {
        throw new Error(
          "Popup blocked - please allow popups for this site to print"
        );
      }

      // Create HTML that follows the BiodataPrintView structure and styling
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Biodata - ${biodata.full_name}</title>
            <meta charset="utf-8">
            <style>
              @media print {
                @page {
                  size: A4;
                  margin: 6mm 8mm 10mm;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
              }
              
              body {
                font-family: Arial, sans-serif;
                font-size: 8.5pt;
                padding: 0;
                max-width: 190mm;
                margin: 0 auto;
              }
              
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 8.2pt;
                margin-bottom: 13px;
              }
              
              th, td {
                border: 1px solid #000;
                padding: 6px 9px;
                text-align: left;
                vertical-align: middle;
              }
              
              th {
                font-weight: bold;
                background-color: #fdfdfd;
                width: 30%;
              }
              
              .section-title {
                font-weight: bold;
                margin-bottom: 4px;
                font-size: 9.5pt;
              }
              
              .header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 9px;
              }
              
              .header img {
                width: 100%;
                height: auto;
                max-width: 182px;
              }
              
              .title {
                text-align: center;
                font-weight: bold;
                font-size: 12.3pt;
                margin-bottom: 9px;
                padding-bottom: 12px;
              }
              
              .content {
                display: flex;
                gap: 11px;
              }
              
              .left-column {
                flex: 1;
              }
              
              .right-column {
                width: 150px;
              }
              
              .photos {
                width: 150px;
                display: flex;
                flex-direction: column;
                gap: 18px;
              }
              
              .photo-box {
                width: 150px;
                border: 1px solid #000;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #f8f8f8;
              }
              
              .passport-photo {
                height: 190px;
              }
              
              .fullbody-photo {
                height: 400px;
              }
              
              .photo-box img {
                max-width: 100%;
                max-height: 100%;
                object-fit: cover;
              }
              
              .status-approved {
                color: #28a745;
                font-weight: bold;
              }
              
              .footer {
                margin-top: 30px;
                padding-top: 15px;
                border-top: 1px solid #dee2e6;
                font-size: 12px;
                color: #6c757d;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div style="width: 182px;">
                <img src="/assets/clab_logo.png" alt="CLAB" onerror="this.style.display='none'">
              </div>
              <div style="width: 182px;">
                <img src="/assets/images/cidb-logo.png" alt="CIDB" onerror="this.style.display='none'">
              </div>
            </div>
            
            <div class="title">FOREIGN CONSTRUCTION LABOUR (FCL) BIODATA - APPROVED</div>
            
            <div class="content">
              <div class="left-column">
                <div class="section-title">1. FCL DETAILS</div>
                <table>
                  <tr><th>Name</th><td colspan="3">${
                    biodata.full_name || ""
                  }</td></tr>
                  <tr><th>Passport No</th><td colspan="3">${
                    biodata.passport_number || ""
                  }</td></tr>
                  <tr><th>Address</th><td colspan="3">${
                    biodata.address || ""
                  }</td></tr>
                  <tr><th>E-Mail Address</th><td colspan="3">${
                    biodata.email || ""
                  }</td></tr>
                  <tr><th>Age</th><td>${
                    biodata.date_of_birth
                      ? new Date().getFullYear() -
                        new Date(biodata.date_of_birth).getFullYear()
                      : ""
                  }</td><th>Date Of Birth</th><td>${
        biodata.date_of_birth
          ? new Date(biodata.date_of_birth).toLocaleDateString("en-GB")
          : ""
      }</td></tr>
                  <tr><th>Marital Status</th><td>${
                    biodata.marital_status || ""
                  }</td><th>Nationality</th><td>${
        biodata.nationality || ""
      }</td></tr>
                  <tr><th>Height</th><td>${
                    biodata.height_cm || ""
                  }</td><th>Weight</th><td>${biodata.weight_kg || ""}</td></tr>
                  <tr><th>Contact Number (Handphone)</th><td colspan="3">${
                    biodata.contact_no || ""
                  }</td></tr>
                </table>
                
                <div class="section-title">2. NEXT OF KIN DETAILS</div>
                <table style="table-layout: fixed;">
                  <tr><th style="width: 43%;">Name</th><td style="width: 57%;">${
                    biodata.next_of_kin?.name || ""
                  }</td></tr>
                  <tr><th>Relationship</th><td>${
                    biodata.next_of_kin?.relationship || ""
                  }</td></tr>
                  <tr><th>Identification No</th><td>${
                    biodata.next_of_kin?.identification_no || ""
                  }</td></tr>
                  <tr><th>Address</th><td>${
                    biodata.next_of_kin?.address || ""
                  }</td></tr>
                  <tr><th>Contact Number</th><td>${
                    biodata.next_of_kin?.contact_no || ""
                  }</td></tr>
                  <tr><th>Bank Account No</th><td>${
                    biodata.next_of_kin?.bank_account_no || ""
                  }</td></tr>
                  <tr><th>Bank Name</th><td>${
                    biodata.next_of_kin?.bank_name || ""
                  }</td></tr>
                </table>
                
                <div class="section-title">3. WORKING DETAILS</div>
                <table style="table-layout: fixed;">
                  <tr><th style="width: 43%;">Highest Qualification</th><td style="width: 57%;">${
                    biodata.education_level || ""
                  }</td></tr>
                  <tr><th>Working Experience in Construction Industry</th><td>${
                    biodata.experience_years || ""
                  } years</td></tr>
                  <tr><th>Skill</th><td>${
                    Array.isArray(biodata.skills)
                      ? biodata.skills.join(", ")
                      : biodata.skills || ""
                  }</td></tr>
                </table>
                
                <div class="section-title">4. STATUS & REVIEW</div>
                <table>
                  <tr><th>Status</th><td><span class="status-approved">APPROVED</span></td></tr>
                  <tr><th>Approval Date</th><td>${
                    biodata.reviewed_at
                      ? new Date(biodata.reviewed_at).toLocaleDateString()
                      : ""
                  }</td></tr>
                  <tr><th>Worker ID</th><td>${
                    biodata.worker_id || "Not assigned"
                  }</td></tr>
                  <tr><th>Recruiter Agency</th><td>${
                    biodata.recruiters?.agency_name || "Unknown"
                  }</td></tr>
                  <tr><th>Medical Status</th><td>${
                    biodata.medical_status || "N/A"
                  }</td></tr>
                  <tr><th>Languages</th><td>${
                    Array.isArray(biodata.language_skills)
                      ? biodata.language_skills.join(", ")
                      : biodata.language_skills || "N/A"
                  }</td></tr>
                </table>
                
                <div class="section-title">5. RECRUITER DETAIL</div>
                <div style="font-size: 10pt; font-weight: bold; padding-left: 4px; margin-bottom: 20px;">${
                  biodata.recruiters?.agency_name || "Unknown"
                }</div>
              </div>
              
              <div class="right-column">
                <div class="photos">
                  <div class="photo-box passport-photo">
                    <img src="${
                      biodata.photos?.[0]?.file_url || ""
                    }" alt="Passport Photo" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=text-align:center;color:#999;font-size:10pt>No Photo</div>'">
                  </div>
                  <div class="photo-box fullbody-photo">
                    <img src="${
                      biodata.photos?.[1]?.file_url || ""
                    }" alt="Full Body Photo" style="width: 100%; height: 100%; object-fit: contain;" onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=text-align:center;color:#999;font-size:10pt>No Photo</div>'">
                  </div>
                </div>
              </div>
            </div>
            
            <div class="footer">
              <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
              <p><strong>System:</strong> eBiodata CLAB - Approved Workers</p>
            </div>
            
            <script>
              // Auto-print after content loads
              setTimeout(function() {
                window.print();
              }, 300);
            </script>
          </body>
        </html>
      `;

      printWindow.document.write(printContent);
      printWindow.document.close();
    } catch (error) {
      console.error("Print error in ClabBiodataApproved:", error);
      notify.error("Print failed: " + error.message);

      // Final fallback: try direct print
      try {
        if (typeof window.print === "function") {
          window.print();
        }
      } catch (finalError) {
        console.error("Final fallback print failed:", finalError);
        notify.error(
          "Unable to print. Please check your browser settings and ensure popups are allowed."
        );
      }
    }
  };

  const handleApprove = async (biodataId, notes = "Approved by CLAB user") => {
    try {
      await clabAPI.approveBiodata(biodataId);
      setBiodataList((prev) => prev.filter((b) => b.id !== biodataId));
      setSelectedBiodataDetails((prev) =>
        prev.filter((b) => b.id !== biodataId)
      );
      showSuccess("Biodata approved successfully");
    } catch (error) {
      showError("Failed to approve biodata");
    }
  };

  const handleReject = async (biodataIds, rejectionReason) => {
    try {
      if (Array.isArray(biodataIds)) {
        await clabAPI.bulkRejectBiodata({
          ids: biodataIds,
          reason_id: rejectionReason.reason_id,
        });
      } else {
        await clabAPI.rejectBiodata(biodataIds, {
          reason_id: rejectionReason.reason_id,
        });
      }
      setBiodataList((prev) =>
        prev.filter((b) => ![].concat(biodataIds).includes(b.id))
      );
      setSelectedBiodataDetails((prev) =>
        prev.filter((b) => ![].concat(biodataIds).includes(b.id))
      );
      setShowDetailsModal(false);
      showSuccess("Biodata rejected successfully");
    } catch (error) {
      showError("Failed to reject biodata");
    }
  };

  useEffect(() => {
    const loadRejectionReasons = async () => {
      try {
        setRejectionReasonsLoading(true);
        const result = await rejectionReasonAPI.getActiveReasons();
        let reasons = [];
        if (Array.isArray(result)) reasons = result;
        else if (Array.isArray(result?.data?.data?.data))
          reasons = result.data.data.data;
        else if (Array.isArray(result?.data?.data)) reasons = result.data.data;
        else if (Array.isArray(result?.data)) reasons = result.data;
        setRejectionReasons(reasons);
      } catch (err) {
        setRejectionReasons([]);
      } finally {
        setRejectionReasonsLoading(false);
      }
    };
    loadRejectionReasons();
  }, []);

  const openRejectModal = (biodata) => {
    setRejectTarget(biodata);
    setRejectForm({ reason_id: "", remarks: "" });
    setShowRejectModal(true);
  };

  const confirmRejectAction = async () => {
    if (!rejectTarget) return;
    if (!rejectForm.reason_id) {
      showError("Please select a rejection reason");
      return;
    }
    try {
      await clabAPI.rejectBiodata(rejectTarget.id, {
        reason_id: Number(rejectForm.reason_id),
        comments: rejectForm.remarks,
      });
      invalidateOnUpdate([rejectTarget.id], "approved", "rejected");
      setBiodataList((prev) => prev.filter((b) => b.id !== rejectTarget.id));
      setShowRejectModal(false);
      setRejectTarget(null);
      showSuccess("Biodata rejected successfully");
    } catch (error) {
      showError("Failed to reject biodata");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderBar
          userRole={userRole}
          userName={userProfile?.full_name || "CLAB User"}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <RoleBasedSidebar
          userRole={userRole}
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <main
          className={`transition-all duration-300 pt-16 ${
            sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
          }`}
        >
          <div className="p-6 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon
                name="Loader2"
                size={48}
                className="animate-spin text-primary mx-auto mb-4"
              />
              <p className="text-text-secondary">Loading approved biodata...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Global Notifier for success/error toasts */}
      <Notifier
        type={
          notifications.length > 0
            ? notifications[notifications.length - 1].type
            : "info"
        }
        message={
          notifications.length > 0
            ? notifications[notifications.length - 1].message
            : ""
        }
        show={notifications.length > 0}
        onClose={() =>
          dismissNotification(notifications[notifications.length - 1]?.id)
        }
        duration={
          notifications.length > 0
            ? notifications[notifications.length - 1].duration
            : 3000
        }
      />
      <HeaderBar
        userRole={userRole}
        userName={userProfile?.full_name || "CLAB User"}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <RoleBasedSidebar
        userRole={userRole}
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`transition-all duration-300 pt-16 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-6">
          <BreadcrumbNavigation userRole={userRole} />

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              Approved Biodata
            </h1>
            <p className="text-text-secondary">
              View approved foreign worker biodata available to employers
            </p>
          </div>

          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <h3 className="font-semibold text-error-800">Error</h3>
              </div>
              <p className="text-error-700 mt-2">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-3 text-sm text-error-600 hover:text-error-800 font-medium"
              >
                Dismiss
              </button>
            </div>
          )}

          <div className="space-y-6">
            {/* Filter Panel */}
            <FilterPanel filters={filters} onFiltersChange={setFilters} />

            {/* Biodata Table */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-text-primary">
                      Approved Biodata (
                      {biodataPagination ? biodataPagination.total : 0})
                    </h2>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="relative group">
                      <button
                        onClick={() => handleBulkPrint(true)}
                        disabled={loading}
                        className="flex items-center space-x-2 px-4 py-2 text-white bg-primary border border-primary-600 rounded-md hover:bg-primary-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Download ALL approved biodata as HTML files in ZIP"
                      >
                        <Icon name="Printer" size={16} />
                        <span>Print All ({biodataPagination?.total || 0})</span>
                      </button>
                    </div>
                    <button
                      onClick={() => handleBulkPrint(false)}
                      disabled={loading || biodataList.length === 0}
                      className="flex items-center space-x-2 px-4 py-2 text-primary bg-primary-50 border border-primary-300 rounded-md hover:bg-primary-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download current page only as HTML files in ZIP"
                    >
                      <Icon name="FileText" size={16} />
                      <span>Print Page ({biodataList.length})</span>
                    </button>
                    <div className="flex flex-col items-end space-y-2">
                      <div className="flex items-center space-x-2">
                        <select
                          value={exportScope}
                          onChange={(e) => setExportScope(e.target.value)}
                          className="px-3 py-2 text-sm border border-secondary-300 rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-primary-300"
                        >
                          <option value="all">
                            All Records (all statuses)
                          </option>
                          <option value="filtered">
                            Apply current filters
                          </option>
                          <option value="current_page">
                            Current page only
                          </option>
                        </select>
                        <button
                          onClick={handleExportData}
                          disabled={exportStatus.isExporting}
                          className="flex items-center space-x-2 px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="Download" size={16} />
                          <span>
                            {exportStatus.isExporting
                              ? "Exporting..."
                              : "Export Excel"}
                          </span>
                        </button>
                      </div>
                      {exportStatus.isExporting ||
                      exportStatus.stage === "downloading" ? (
                        <p className="text-xs text-text-secondary">
                          Exporting {exportStatus.totalRecords || "records"}
                          {exportStatus.estimatedSeconds
                            ? ` (~${Math.max(
                                1,
                                Math.round(exportStatus.estimatedSeconds)
                              )}s)`
                            : ""}{" "}
                          {exportStatus.downloadedBytes
                            ? `• ${
                                Math.round(
                                  (exportStatus.downloadedBytes / 1024 / 1024) *
                                    100
                                ) / 100
                              } MB downloaded`
                            : ""}
                        </p>
                      ) : exportStatus.stage === "completed" ? (
                        <p className="text-xs text-success-600">
                          Last export: {exportStatus.totalRecords || 0} records
                          → {exportStatus.filename || "download"}
                        </p>
                      ) : exportStatus.stage === "error" ? (
                        <p className="text-xs text-error-600">
                          Export failed: {exportStatus.error}
                        </p>
                      ) : null}
                    </div>
                    <button
                      onClick={() => window.location.reload()}
                      className="flex items-center space-x-2 px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150"
                    >
                      <Icon name="RefreshCw" size={16} />
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>
              </div>

              {paginatedBiodata.length === 0 ? (
                <div className="p-12 text-center">
                  <Icon
                    name="FileText"
                    size={48}
                    className="text-secondary-300 mx-auto mb-4"
                  />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    No Approved Biodata
                  </h3>
                  <p className="text-text-secondary">
                    No approved biodata available.
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary-50 border-b border-secondary-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Worker No
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Passport No
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Nationality
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Skills
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Registered Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Medical Expiry
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            VDR Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Approval Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Reviewer
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-surface divide-y divide-secondary-200">
                        {paginatedBiodata.map((biodata) => (
                          <tr
                            key={biodata.id}
                            className="hover:bg-secondary-50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 font-mono text-xs">
                              {biodata.worker_id || "-"}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div
                                  className="w-10 h-10 rounded-full overflow-hidden bg-secondary-100"
                                  data-biodata-id={biodata.id}
                                >
                                  <Image
                                    src={getPhotoUrl(biodata.id, biodata)}
                                    alt={biodata.full_name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-text-primary">
                                    {biodata.full_name}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.passport_no || "-"}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.nationality || "-"}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {Array.isArray(biodata.skills)
                                ? biodata.skills.join(", ")
                                : biodata.skills || biodata.skill || "-"}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.created_at
                                ? new Date(
                                    biodata.created_at
                                  ).toLocaleDateString()
                                : "-"}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.medical_expiry_date
                                ? new Date(
                                    biodata.medical_expiry_date
                                  ).toLocaleDateString()
                                : "-"}
                            </td>
                            <td className="px-6 py-4">
                              {biodata.vdr_type && (
                                <span className="inline-flex items-center px-2 py-1 bg-accent-100 text-accent-800 rounded-full text-xs font-medium">
                                  <Icon
                                    name="Briefcase"
                                    size={12}
                                    className="mr-1"
                                  />
                                  {biodata.vdr_type}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.reviewed_at
                                ? new Date(
                                    biodata.reviewed_at
                                  ).toLocaleDateString()
                                : "-"}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.reviewed_by_profile?.full_name || "-"}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleViewDetails(biodata)}
                                  className="p-2 text-accent hover:bg-accent-50 rounded-md transition-colors duration-150"
                                  title="View Details"
                                >
                                  <Icon name="Eye" size={16} />
                                </button>
                                <button
                                  onClick={() => openRejectModal(biodata)}
                                  className="p-2 text-error hover:bg-error-50 rounded-md transition-colors duration-150"
                                  title="Reject Approved Biodata"
                                >
                                  <Icon name="XCircle" size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Component */}
                  {biodataPagination && (
                    <div className="px-6 py-4 border-t border-secondary-200">
                      <PaginationComponent
                        pagination={{
                          currentPage: biodataPagination.page || currentPage,
                          perPage: biodataPagination.limit || itemsPerPage,
                          total: biodataPagination.total || 0,
                          totalPages:
                            biodataPagination.totalPages ||
                            biodataPagination.total_pages ||
                            Math.ceil(
                              (biodataPagination.total || 0) /
                                (biodataPagination.limit || itemsPerPage)
                            ),
                          from:
                            ((biodataPagination.page || currentPage) - 1) *
                              (biodataPagination.limit || itemsPerPage) +
                            1,
                          to: Math.min(
                            (biodataPagination.page || currentPage) *
                              (biodataPagination.limit || itemsPerPage),
                            biodataPagination.total || 0
                          ),
                        }}
                        onPageChange={setCurrentPage}
                        onPerPageChange={setItemsPerPage}
                        showPerPageSelector={true}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Biodata Details Overlay using shared viewer */}
      {showDetailsModal && selectedBiodataDetails && (
        <>
          <style>{`
            @media print {
              .biodata-modal-overlay {
                display: none !important;
              }
              .biodata-modal-content {
                position: static !important;
                max-width: 100% !important;
                max-height: none !important;
                box-shadow: none !important;
              }
            }
          `}</style>
          <div className="biodata-modal-overlay fixed inset-0 z-1001 bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4">
            <div className="biodata-modal-content bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-secondary-200 flex items-center justify-between">
                <h2 className="text-xl font-bold">Approved Biodata Details</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedBiodataDetails(null);
                    }}
                    className="text-secondary-600 hover:text-secondary-900"
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="p-6">
                <BiodataDetailsView biodata={selectedBiodataDetails} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Reject Approved Biodata Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-1001 bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Reject Approved Biodata</h3>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectTarget(null);
                }}
                className="text-secondary-600 hover:text-secondary-900"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Rejection Reason
                </label>
                <select
                  value={rejectForm.reason_id}
                  onChange={(e) =>
                    setRejectForm((prev) => ({
                      ...prev,
                      reason_id: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-error-300 focus:border-transparent"
                >
                  <option value="">Select a reason...</option>
                  {rejectionReasons.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.reason || r.name || `Reason ${r.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Comments (optional)
                </label>
                <textarea
                  value={rejectForm.remarks}
                  onChange={(e) =>
                    setRejectForm((prev) => ({
                      ...prev,
                      remarks: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-error-300 focus:border-transparent"
                  rows={3}
                  placeholder="Add any notes for the rejection"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectTarget(null);
                }}
                className="px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRejectAction}
                disabled={rejectionReasonsLoading}
                className="px-4 py-2 text-white bg-error rounded-md hover:bg-error-700 disabled:opacity-50"
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClabBiodataApproved;
